import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react"
import { DateTime } from "luxon"

import { useDateTime } from "../../../../../modules/localization/useDateTime"

import type { TakeawayCartItem, TakeawayKitchen, TakeawayProduct } from "./takeawayTypes"

export type TakeawayCartContextValue = Readonly<{
    cartByDate: Record<string, TakeawayCartItem[]>
    cartForSelectedDate: TakeawayCartItem[]
    selectedKitchen: TakeawayKitchen | null
    selectedDate: DateTime
    showCheckout: boolean
    deliveryKitchenName: string | null
    onShowOrder: () => void
    onCloseCheckout: () => void
    onClearCart: () => void
    onQuantityChange: (product: TakeawayProduct, quantity: number, dateKey?: string) => void
    onKitchenSelect: (kitchen: TakeawayKitchen) => void
    onDateChange: (date: DateTime) => void
    onMenuKitchenLoaded: (name: string) => void
}>

export const TakeawayCartContext = createContext<TakeawayCartContextValue | null>(null)

export function useTakeawayCart(): TakeawayCartContextValue | null {
    return useContext(TakeawayCartContext)
}

export function TakeawayCartProvider({ children }: PropsWithChildren) {
    const dt = useDateTime()

    const [selectedKitchen, setSelectedKitchen] = useState<TakeawayKitchen | null>(null)
    const [selectedDate, setSelectedDate] = useState<DateTime>(() => dt.now())
    const [cartByDate, setCartByDate] = useState<Record<string, TakeawayCartItem[]>>({})
    const [showCheckout, setShowCheckout] = useState(false)
    const [deliveryKitchenName, setDeliveryKitchenName] = useState<string | null>(null)

    const cartForSelectedDate = useMemo(
        () => cartByDate[dt.toDate(selectedDate)] ?? [],
        [cartByDate, dt, selectedDate],
    )

    const handleQuantityChange = useCallback(
        (product: TakeawayProduct, quantity: number, forDateKey?: string) => {
            const dateKey = forDateKey ?? dt.toDate(selectedDate)
            setCartByDate((prev) => {
                const items = prev[dateKey] ?? []
                const existing = items.find((item) => item.product.id === product.id)

                let updated: TakeawayCartItem[]
                if (existing) {
                    if (quantity <= 0) {
                        updated = items.filter((item) => item.product.id !== product.id)
                    } else {
                        updated = items.map((item) =>
                            item.product.id === product.id ? { ...item, quantity } : item,
                        )
                    }
                } else {
                    if (quantity <= 0) return prev
                    updated = [...items, { product, quantity }]
                }

                if (!updated.length) {
                    const { [dateKey]: _, ...rest } = prev
                    return rest
                }
                return { ...prev, [dateKey]: updated }
            })
        },
        [dt, selectedDate],
    )

    return (
        <TakeawayCartContext.Provider
            value={{
                cartByDate,
                cartForSelectedDate,
                selectedKitchen,
                selectedDate,
                showCheckout,
                deliveryKitchenName,
                onShowOrder: () => setShowCheckout(true),
                onCloseCheckout: () => setShowCheckout(false),
                onClearCart: () => setCartByDate({}),
                onQuantityChange: handleQuantityChange,
                onKitchenSelect: setSelectedKitchen,
                onDateChange: setSelectedDate,
                onMenuKitchenLoaded: setDeliveryKitchenName,
            }}
        >
            {children}
        </TakeawayCartContext.Provider>
    )
}
